import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthProvider';
import { userRoleLabels } from '@/schemas/auth/auth.schemas';
import { Button } from '@/components/ui/button';

/**
 * Full-screen unauthorized access page.
 *
 * Renders over the entire viewport (including sidebar/navbar) when the user
 * lacks the required permission for a page or route.
 *
 * Reusable across all pages — reads the current role from AuthContext
 * to display contextual information.
 */
export function UnauthorizedPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { currentTenantAccess } = useAuth();
  const navigate = useNavigate();

  const role = currentTenantAccess?.role;
  const roleName = role ? t(userRoleLabels[role]) : null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-background overflow-hidden">
      <div className="text-center max-w-md w-full px-6 py-8">
        {/* Warning Icon */}
        <div className="mb-4">
          <svg
            className="mx-auto h-16 w-16 text-amber-500"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>

        {/* Heading */}
        <h1 className="text-2xl font-bold text-foreground mb-2">{t('unauthorized.title')}</h1>

        {/* Description */}
        <p className="text-muted-foreground text-base mb-6">{t('unauthorized.description')}</p>

        {/* Info Card */}
        <div className="bg-card border border-border rounded-xl p-4 shadow-lg mb-6">
          <div className="space-y-3 text-sm">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg
                  className="h-5 w-5 text-muted-foreground"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="text-left flex-1">
                <p className="text-muted-foreground text-xs font-medium mb-1">
                  {t('unauthorized.your_role')}
                </p>
                <p className="font-semibold text-foreground text-sm">{roleName ?? '—'}</p>
              </div>
            </div>

            <div className="border-t border-border pt-3">
              <p className="text-amber-600 dark:text-amber-500 text-xs font-medium leading-relaxed">
                {t('unauthorized.help_text')}
              </p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={(): void => {
            navigate('/');
          }}
        >
          {t('unauthorized.back_to_dashboard')}
        </Button>
      </div>
    </div>
  );
}
